import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { FlowStep, fmtNum } from '../../data/onboardingFunnel';
interface FunnelChartProps {
  steps: FlowStep[];
}

/**
 * The whole flow in one list: the Sign in click, every screen after it, and the
 * drop-off between each point. Grouped by stage, indexed to the CTA click.
 */
export function FunnelChart({
  steps
}: FunnelChartProps) {
  const top = steps[0].users;
  return <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white" data-id="element-573" data-mp="2nj6007y3me87">
      <div className="flex items-center gap-3 border-b border-neutral-100 bg-neutral-50 px-4 py-2 text-[10.5px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-574" data-mp="2shh8ae8qwgob">
        <span className="flex-1" data-id="element-575" data-mp="2ij1g07xzf1d8">Step</span>
        <span className="w-20 text-right" data-id="element-576" data-mp="2ik2ukme7i5zp">Reached</span>
        <span className="hidden w-16 text-right sm:block" data-id="element-577" data-mp="bsliixx80fq3">% of CTA</span>
        <span className="w-24 text-right" data-id="element-578" data-mp="gqd99om4pvsp">Dropped</span>
        <span className="hidden w-20 text-right md:block" data-id="element-579" data-mp="gpu1h0q70nse">Median</span>
      </div>

      <ol data-id="element-580" data-mp="12vag62hywyfm">
        {steps.map((step, i) => {
        const prev = i > 0 ? steps[i - 1] : null;
        const lost = prev ? prev.users - step.users : 0;
        const lostPct = prev ? Math.round(lost / prev.users * 100) : 0;
        const per100 = step.users / top * 100;
        const newSection = i === 0 || steps[i - 1].section !== step.section;
        return <React.Fragment key={step.id} data-id="element-581" data-mp="jxhef3j2yujt" data-mpi={i}>
              {newSection && <li className="border-t border-neutral-100 bg-neutral-50/60 px-4 py-1.5 text-[10.5px] font-bold uppercase tracking-wider text-neutral-400 first:border-t-0" data-id="element-582" data-mp="jxzjmpnq0j3v">
                  {step.section}
                </li>}
              <li className="border-t border-neutral-100 px-4 py-3" data-id="element-583" data-mp="owu0hm24nxlc">
                <div className="flex items-center gap-3" data-id="element-584" data-mp="oviwpm8ompu8">
                  <div className="min-w-0 flex-1" data-id="element-585" data-mp="24ct72sw3jljh">
                    <p className="text-[14px] font-semibold" data-id="element-586" data-mp="24fnpkevjuwa9">{step.label}</p>
                    <p className="text-[12px] text-neutral-500" data-id="element-587" data-mp="20bfxc7pvuy7a">
                      {step.definition}
                    </p>
                  </div>
                  <span className="w-20 shrink-0 text-right font-mono text-[13px] font-semibold tabular-nums" data-id="element-588" data-mp="11zwn6sblnba7">
                    {fmtNum(step.users)}
                  </span>
                  <span className="hidden w-16 shrink-0 text-right font-mono text-[12.5px] text-neutral-500 sm:block" data-id="element-589" data-mp="16xeyuqh044lw">
                    {per100 >= 10 ? Math.round(per100) : per100.toFixed(1)}%
                  </span>
                  <span className={`w-24 shrink-0 text-right font-mono text-[12.5px] font-semibold tabular-nums ${lostPct >= 30 ? 'text-red-600' : 'text-neutral-400'}`} data-id="element-590" data-mp="j9f6jm41k657">
                    {prev ? `−${lostPct}%` : '—'}
                  </span>
                  <span className="hidden w-20 shrink-0 items-center justify-end gap-1 text-right font-mono text-[12.5px] text-neutral-500 md:flex" data-id="element-591" data-mp="s3ju9wnvp1gi">
                    {step.medianTime !== '—' && <Clock size={10} data-id="element-592" data-mp="3vq0nq5t6ie6x" />}
                    {step.medianTime}
                  </span>
                </div>

                <div className="mt-2 flex items-center gap-2" data-id="element-593" data-mp="3o716o6t6wyqq">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-100" data-id="element-594" data-mp="3j881d1o24e1n">
                    <motion.div initial={false} animate={{
                  width: `${Math.max(per100, 0.8)}%`
                }} transition={{
                  duration: 0.4
                }} className="h-full rounded-full bg-[#2B211A]" data-id="element-595" data-mp="3f6cznw27rync" />
                  </div>
                  {prev && lost > 0 && <span className="w-28 shrink-0 text-right text-[11.5px] text-neutral-400" data-id="element-596" data-mp="30i8fennijf2g">
                      {fmtNum(lost)} lost here
                    </span>}
                </div>

                {!step.tracked && <p className="mt-1.5 text-[11.5px] text-[#C2410C]" data-id="element-597" data-mp="2rhtsjjuwhrwu">
                    Estimated — needs{' '}
                    <code className="font-mono" data-id="element-598" data-mp="2migzghdfrolt">{step.event}</code>
                  </p>}
              </li>
            </React.Fragment>;
      })}
      </ol>
    </div>;
}